import {
  Backdrop, Button, CircularProgress, Divider, Grid,
  Paper, Skeleton, Typography
} from '@mui/material';
import { HeadlinePage, TableCustom } from 'components/common';
import { useList, useUserManager } from 'components/hooks';
import { Input } from 'components/layout';
import { ROUTES } from 'consts';
import { userPolicies } from 'helpers';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStyle } from './styles';
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

export default function UserFormDumb({
  defaultValues,
  handleOnSubmit,
  isEdit,
}) {
  const { getContentList, content } = useList('roles');
  const {isLoaded} = useUserManager();
  const [t] = useTranslation();
  const classes = useStyle();
  const [roles, setRoles] = useState(defaultValues.roles);
  const [roleOptions, setRolesOptions] = useState([]);
  const [showRoles, setShowRoles] = useState(true);


  const methods = useForm({
    defaultValues: useMemo(() => {
      return defaultValues;
    }, [defaultValues])
  });

  const { reset, control, handleSubmit, setValue, getValues, setError } = methods;

  useEffect(() => {
    getContentList({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (content) {
      const roles = content.map(c => ({value: c.id, label: c.label}))
      setRolesOptions(roles);
      if (roles && roles.length>0 && roles[0].value){
        setValue('role', roles[0].value);
      }
      changeIsAdmin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  useEffect(() => {
    if (isEdit) {
      const roles = defaultValues.roles.map(c => ({
        id: c.roleId,
        label: c.roleName,
        text: c.roleDescription,
      }));
      setRoles(roles);
      reset(defaultValues, roles);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const onDelete = (role) => {
    setRoles(roles.filter(r => r.id !== role.id));
  };

  const addRole = () => {
    const roleFound = roles.find(r => r.id === Number(getValues('role')));
    if (roleFound) {
      setError('role', { message: t('userList.form.error.role') });
      return;
    } else {
      const role = content.find(r => r.id === Number(getValues('role')));
      const list = [...roles, role];
      setRoles(list);
    }
  };
  
  const changeIsAdmin = () => {
    setShowRoles(!getValues().admin); 
  }


  const fields = [
    { policy: userPolicies.firstname },
    { policy: userPolicies.surname },
    { policy: userPolicies.username },
    { policy: userPolicies.email },
    { policy: userPolicies.password, isEdit },
    { policy: userPolicies.confirmPassword, isEdit },
    { policy: userPolicies.active },
    { policy: userPolicies.admin , handleOnChange: changeIsAdmin },

  ];

  const columns = [
    { label: 'Name', key: 'label' },
    { label: 'Description', key: 'text' },
  ];

  const actions = [
    {
      label: t('roleList.components.table.delete'),
      icon: <HighlightOffIcon color="primary" />,
      handle: onDelete,
    },
  ];

  const prepareData = (data) => {
    if (!isEdit && data && data.password !== data.confirmPassword) {
      setError(userPolicies.confirmPassword.name, {message: t('changePassword.form.error.equalPassword')})
      return;
    }
    const roleList = roles.map(r => r.id);
    const dataSave = {
      username: data.username,
      firstname: data.firstname,
      surname: data.surname,
      email: data.email,
      password: data.password,
      active: data.active,
      admin: data.admin,
      permissions: [],
      roles: [...roleList]
    }
    handleOnSubmit(dataSave);
  }


  return (
    <>
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoaded.post || isLoaded.put}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      <HeadlinePage
        title={isEdit ? 'userEdit.headline' : 'userCreate.headline'}
        goBack={ROUTES.USER_LIST}
      />

      <Paper className={classes.paperStyle}>
        <FormProvider { ...methods }>
          <form onSubmit={handleSubmit(prepareData)}>
            <Typography variant="h6" sx={{ padding: '8px 0px' }}>
              {t('userList.form.title')}
            </Typography>

              <Grid container spacing={2}>
                {fields.map((field) => (
                  <React.Fragment key={field.policy.id}>
                  { content && !field.isEdit ?  (
                      <Grid item xs={12} sm={6} md={4} lg={4} key={field.id}>
                        <Input
                          {...field.policy}
                          control={control}
                          options={field.options}
                          sx={{ width: '100%' }}
                          onChange = { field.handleOnChange || (() => {}) }
                        />
                      </Grid>
                    ) : !field.isEdit && (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={field.id}>
                        <Skeleton
                          sx={{ marginBottom: 1 }}
                          variant="text"
                          animation="wave"
                          width="60%"
                        />
                        <Skeleton height="40px" variant="rounded" animation="wave" />
                      </Grid>
                    )
                  }
                  </React.Fragment>
                ))}
              </Grid>
              {showRoles?
                <>
              <Grid container spacing={2} sx={{alignItems: 'flex-end', padding: '8px 0px'}}>

                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Divider/>
                  <Typography variant="h6" sx={{ padding: '8px 0px' }}>
                    {t('userList.form.subtitle')}
                  </Typography>
                </Grid>

              </Grid>

              <Grid container spacing={2} sx={{alignItems: 'flex-start'}}>

                <Grid item xs={6} sm={6} md={6} lg={6} >
                  { roleOptions.length > 0 ?
                    (
                      <>
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                          <Input
                            { ...userPolicies.role }
                            control={ control }
                            options={ roleOptions }
                            sx={{ width: '100%' }}
                          />
                        </Grid>

                      </>
                    ) : (
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Skeleton
                          sx={{ marginBottom: 1 }}
                          variant="text"
                          animation="wave"
                          width="60%"
                        />
                        <Skeleton height="40px" variant="rounded" animation="wave" />
                      </Grid>
                    )
                  }
                </Grid>
                
                <Grid item xs={12} sm={4} md={4} lg={4}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    height: '84px',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={addRole}
                  >
                    {t('userList.components.addRole')}
                  </Button>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12}>
                <TableCustom
                    columns ={ columns }
                    content ={ roles }
                    actions ={ actions }
                  />
                </Grid>
                

              </Grid>
              </>
                :null}
              <Grid container sx={{ mt: 4 }}>
                <Grid item md={2} lg={3} />
                <Grid item xs={12} sm={12} md={8} lg={6}>
                  <Button variant="contained" sx={{ width: '100%' }} type='submit' >
                    {isEdit ? t('userEdit.components.submit') : t('userCreate.components.submit')}
                  </Button>
                </Grid>
                <Grid item md={2} lg={3} />
              </Grid>
          </form>
        </FormProvider>
      </Paper>
    </>
  )
}
